import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, SectionTag } from "@/components/Reveal";
import { useSiteContent } from "@/context/ContentContext";

export default function IndustryPage() {
    const { industries, caseStudies, CONTENT_LOADED } = useSiteContent();
    const { slug } = useParams();
    const industry = industries.find((i) => i.slug === slug);
    if (!industry && !CONTENT_LOADED) return <div className="min-h-screen" />;
    if (!industry) return <Navigate to="/work" replace />;
    const proof = caseStudies.filter((c) => industry.clients.includes(c.slug));
    const others = industries.filter((i) => i.slug !== slug);

    return (
        <div data-testid="industry-page">
            <SEO title={industry.seoTitle} description={industry.seoDescription} path={`/industries/${industry.slug}`} />

            <header className="mx-auto max-w-7xl px-6 pb-14 pt-36 lg:px-10">
                <Reveal>
                    <Link
                        to="/work"
                        data-testid="industry-back-link"
                        className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 transition-colors duration-300 hover:text-neon"
                    >
                        <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
                        All Work
                    </Link>
                    <div className="mt-10">
                        <SectionTag>Industry — {industry.label}</SectionTag>
                        <h1 className="mt-6 max-w-4xl font-display text-4xl font-black leading-tight tracking-tight md:text-6xl">
                            {industry.h1}
                        </h1>
                        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-zinc-400 md:text-xl">
                            {industry.intro}
                        </p>
                    </div>
                </Reveal>
            </header>

            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <Reveal>
                    <div className="clip-frame overflow-hidden border border-white/10">
                        <img
                            src={industry.image}
                            alt={industry.imageAlt}
                            data-testid="industry-hero-image"
                            className="aspect-[16/9] w-full object-cover object-top"
                        />
                    </div>
                </Reveal>

                <div className="mt-20 grid gap-12 lg:grid-cols-12">
                    <Reveal className="lg:col-span-5">
                        <div className="lg:sticky lg:top-32">
                            <SectionTag>The Checklist</SectionTag>
                            <h2 className="mt-6 font-display text-2xl font-bold leading-tight md:text-3xl">
                                What a {industry.label.toLowerCase()} website has to do
                            </h2>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-7">
                        <ul className="grid gap-4 sm:grid-cols-2">
                            {industry.needs.map((n, i) => (
                                <li
                                    key={n}
                                    data-testid={`industry-need-${i}`}
                                    className="flex items-center gap-3 border border-white/10 bg-surface px-4 py-4 text-sm text-zinc-300"
                                >
                                    <Check className="h-4 w-4 shrink-0 text-neon" />
                                    {n}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-10 space-y-6">
                            {industry.paragraphs.map((p, i) => (
                                <p key={i} className="leading-relaxed text-zinc-400">{p}</p>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </div>

            {proof.length > 0 && (
            <section data-testid="industry-proof-section" className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
                <Reveal>
                    <SectionTag>Proof</SectionTag>
                    <h2 className="mt-6 font-display text-2xl font-bold tracking-tight md:text-4xl">
                        Built by Camarca. Live right now.
                    </h2>
                </Reveal>
                <div className={`mt-12 grid gap-6 ${proof.length > 1 ? "md:grid-cols-2" : ""}`}>
                    {proof.map((c, i) => (                        <Reveal key={c.slug} delay={i * 0.1}>
                            <Link
                                to={`/work/${c.slug}`}
                                data-testid={`industry-proof-card-${i}`}
                                className="group relative block overflow-hidden border border-white/10 clip-frame transition-[border-color] duration-500 hover:border-neon/50"
                            >
                                <img
                                    src={c.image}
                                    alt={c.alt}
                                    loading="lazy"
                                    className="aspect-video w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black via-black/70 to-transparent p-6">
                                    <div className="flex w-full items-end justify-between gap-4">
                                        <div>
                                            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-neon">{c.category}</p>
                                            <h3 className="mt-2 font-display text-lg font-bold md:text-xl">{c.name}</h3>
                                        </div>
                                        <ArrowRight className="h-5 w-5 shrink-0 text-neon transition-transform duration-300 group-hover:translate-x-1.5" />
                                    </div>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>
            )}

            <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
                <Reveal className="flex flex-wrap items-center justify-between gap-8 border border-white/10 bg-surface p-8 md:p-10">
                    <div>
                        <h2 className="font-display text-2xl font-bold md:text-3xl">{industry.cta}</h2>
                        <p className="mt-3 max-w-lg text-zinc-400">
                            Tell me about your business and I'll build you a site that brings customers to you.
                        </p>
                    </div>
                    <Link
                        to="/contact"
                        data-testid="industry-cta"
                        className="group flex items-center gap-3 bg-neon px-8 py-4 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_25px_rgba(0,240,255,0.5)]"
                    >
                        Start a Project
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </Reveal>
            </section>

            <div className="border-t border-white/10">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-10 gap-y-4 px-6 py-10 lg:px-10">
                    <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-600">More industries</span>
                    {others.map((o) => (
                        <Link
                            key={o.slug}
                            to={`/industries/${o.slug}`}
                            data-testid={`industry-link-${o.slug}`}
                            className="group flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-zinc-400 transition-colors duration-300 hover:text-neon"
                        >
                            {o.label}
                            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
